import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Input,
  Button,
  Spin,
  Typography,
  Alert,
  List,
  Rate,
  Select,
  Table,
  Space,
  Avatar,
} from "antd";
import {
  SendOutlined,
  UserOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "./TeacherAgents.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const FeedbackTeacherAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch students and assignments data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        // Fetch students
        const studentsRes = await api.get("/teacher/students", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (studentsRes.data) {
          setStudents(studentsRes.data);
        }

        // Fetch assignments
        const assignmentsRes = await api.get("/teacher/assignments", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (assignmentsRes.data) {
          setAssignments(assignmentsRes.data);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      return;
    }

    const connectWebSocket = () => {
      const ws = new WebSocket(
        `ws://localhost:8000/ws/agent/feedback?token=${token}`
      );
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected - Feedback Agent");
        setConnected(true);
        setError(null);

        // Send initial message
        ws.send(
          JSON.stringify({
            message: "Hello, I need help with student feedback.",
            context: "student_feedback",
          })
        );
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setMessages((prev) => [
          ...prev,
          {
            text: data.message,
            sender: "agent",
            timestamp: new Date().toLocaleTimeString(),
            feedback: data.feedback || null,
          },
        ]);
        setLoading(false);
      };

      ws.onerror = (e) => {
        console.error("WebSocket error:", e);
        setError("Connection error. Please try again.");
        setConnected(false);
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);

        // Attempt to reconnect after a delay
        setTimeout(() => {
          if (socketRef.current?.readyState === WebSocket.CLOSED) {
            connectWebSocket();
          }
        }, 3000);
      };
    };

    connectWebSocket();

    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  // Handle sending messages
  const handleSend = () => {
    if (
      !input.trim() ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    )
      return;

    const message = {
      text: input,
      sender: "user",
      timestamp: new Date().toLocaleTimeString(),
      studentId: selectedStudent,
    };

    setMessages((prev) => [...prev, message]);
    setLoading(true);
    setInput("");

    socketRef.current.send(
      JSON.stringify({
        message: input,
        context: "student_feedback",
        studentId: selectedStudent,
      })
    );
  };

  // Handle pressing Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Mock data for student assignments - would be replaced with real data in production
  const mockStudentData = [
    {
      key: "1",
      name: "John Doe",
      assignment: "Biology Quiz - Cell Structure",
      status: "Submitted",
      score: 85,
      date: "2023-05-15",
    },
    {
      key: "2",
      name: "Jane Smith",
      assignment: "Physics Lab - Motion",
      status: "Graded",
      score: 92,
      date: "2023-05-14",
    },
    {
      key: "3",
      name: "Michael Brown",
      assignment: "Chemistry Homework - Periodic Table",
      status: "Late",
      score: 78,
      date: "2023-05-12",
    },
    {
      key: "4",
      name: "Emma Wilson",
      assignment: "Math Test - Algebra",
      status: "Submitted",
      score: null,
      date: "2023-05-16",
    },
  ];

  const columns = [
    {
      title: "Student",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          {text}
        </Space>
      ),
    },
    {
      title: "Assignment",
      dataIndex: "assignment",
      key: "assignment",
      render: (text) => (
        <Space>
          <FileTextOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "blue";
        if (status === "Graded") color = "green";
        if (status === "Late") color = "orange";
        return <span style={{ color }}>{status}</span>;
      },
    },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      render: (score) => (score === null ? "Pending" : `${score}/100`),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            onClick={() => {
              setSelectedStudent(record.key);
              setInput(
                `Generate feedback for ${record.name}'s assignment: ${record.assignment}`
              );
            }}
          >
            Generate Feedback
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div className="teacher-agent-container">
        <Card
          className="teacher-agent-card"
          title={
            <div className="agent-header">
              <Title level={3}>Feedback Assistant</Title>
              {connected ? (
                <Text type="success" className="connection-status">
                  Connected
                </Text>
              ) : (
                <Text type="danger" className="connection-status">
                  Disconnected
                </Text>
              )}
            </div>
          }
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              className="error-alert"
            />
          )}

          <div className="feedback-container">
            <div className="student-selector">
              <Card title="Student Assignments" className="student-card">
                <Table
                  dataSource={mockStudentData}
                  columns={columns}
                  pagination={false}
                  size="small"
                />
              </Card>
            </div>

            <div className="message-container">
              {messages.length === 0 ? (
                <div className="welcome-message">
                  <Title level={4}>Welcome to Feedback Assistant!</Title>
                  <Text>
                    I can help you generate personalized feedback for student
                    assignments. Select a student from the list or simply ask me
                    for feedback suggestions.
                  </Text>
                  <Paragraph className="instructions">
                    <ul>
                      <li>Generate constructive feedback for assignments</li>
                      <li>Get suggestions for improvement areas</li>
                      <li>Create personalized comments for reports</li>
                      <li>Analyze student performance trends</li>
                    </ul>
                  </Paragraph>
                </div>
              ) : (
                <List
                  itemLayout="horizontal"
                  dataSource={messages}
                  renderItem={(message) => (
                    <div
                      className={`message ${
                        message.sender === "user"
                          ? "user-message"
                          : "agent-message"
                      }`}
                    >
                      <div className="message-content">
                        <div className="message-text">{message.text}</div>
                        {message.feedback && (
                          <div className="feedback-template">
                            <Card size="small" className="feedback-card">
                              <Text strong>Generated Feedback Template:</Text>
                              <Paragraph>{message.feedback}</Paragraph>
                            </Card>
                          </div>
                        )}
                        <div className="message-timestamp">
                          {message.timestamp}
                        </div>
                      </div>
                    </div>
                  )}
                />
              )}
              {loading && (
                <div className="agent-message">
                  <div className="message-content">
                    <div className="loading-indicator">
                      <Spin size="small" />{" "}
                      <Text italic>Generating feedback...</Text>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="input-area">
            <TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask for feedback suggestions or select a student from the list..."
              autoSize={{ minRows: 2, maxRows: 6 }}
              disabled={!connected}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!connected || !input.trim()}
              className="send-button"
            >
              Send
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default FeedbackTeacherAgent;
